/**
 * The help menu: the "?" at the foot of the rail, above the account.
 *
 * Where to read, who to ask, the shortcuts, and what changed lately — the
 * things people look for when they are stuck or curious, in one quiet place.
 * It is a list you can type into: start typing and it becomes a search of its
 * own rows, arrow keys move, Enter goes.
 *
 * @module components/HelpMenu
 * @see ./helpMenu.logic for the entries and "What's new"
 */
import { useAtomValue } from "@effect/atom-react";
import { useNavigate } from "@tanstack/react-router";
import {
  BookOpenIcon,
  CircleCheckIcon,
  KeyboardIcon,
  MessageCircleIcon,
  SearchIcon,
  SettingsIcon,
  WandSparklesIcon,
  type LucideIcon,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from "react";

import { openCommandPalette } from "../commandPaletteBus";
import { openGuide } from "../guideBus";
import { shortcutLabelForCommand } from "../keybindings";
import { cn } from "../lib/utils";
import { orgUrlSegment } from "../orgRouting";
import { useActiveOrgId } from "../state/orgs";
import { primaryServerKeybindingsAtom } from "../state/server";
import { HELP_ENTRIES, searchHelp, WHATS_NEW, type HelpEntry } from "./helpMenu.logic";
import { Popover, PopoverPopup, PopoverTrigger } from "./ui/popover";
import { QUICK_MENU_POPUP_CLASS, QUICK_MENU_VIEWPORT_CLASS, QuickMenuRow } from "./ui/quick-menu";
import { Tooltip, TooltipPopup, TooltipTrigger } from "./ui/tooltip";

const ICONS: Record<string, LucideIcon> = {
  docs: BookOpenIcon,
  guide: WandSparklesIcon,
  search: SearchIcon,
  shortcuts: KeyboardIcon,
  contact: MessageCircleIcon,
  status: CircleCheckIcon,
  settings: SettingsIcon,
};

export function HelpMenu() {
  const navigate = useNavigate();
  const keybindings = useAtomValue(primaryServerKeybindingsAtom);
  const activeOrgId = useActiveOrgId();
  const orgSegment = activeOrgId ? orgUrlSegment(activeOrgId) : null;

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const searching = query.trim().length > 0;

  // One flat list drives the keyboard, whether it is the menu or its results.
  const rows = useMemo<ReadonlyArray<HelpEntry>>(
    () => (searching ? searchHelp(query) : [...HELP_ENTRIES, ...WHATS_NEW]),
    [query, searching],
  );

  useEffect(() => {
    if (!open) return;
    setQuery("");
    setActiveIndex(0);
    // Focus the (collapsed) field so the first keystroke already searches.
    requestAnimationFrame(() => inputRef.current?.focus());
  }, [open]);

  const go = (entry: HelpEntry) => {
    setOpen(false);
    const { destination } = entry;
    if (destination.kind === "external") {
      window.open(destination.href, destination.href.startsWith("mailto:") ? "_self" : "_blank", "noopener");
    } else if (destination.kind === "route") {
      void navigate({ to: destination.path(orgSegment) } as never);
    } else {
      // After this menu has closed and handed focus back to its button:
      // opening another popover in the same tick gets it closed again, because
      // that focus change reads as a click outside it.
      const run = destination.action === "guide" ? openGuide : openCommandPalette;
      window.setTimeout(run, 120);
    }
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      if (rows.length === 0) return;
      const step = event.key === "ArrowDown" ? 1 : -1;
      setActiveIndex((index) => (index + step + rows.length) % rows.length);
    } else if (event.key === "Enter") {
      event.preventDefault();
      const entry = rows[activeIndex];
      if (entry) go(entry);
    } else if (event.key === "Escape" && searching) {
      event.preventDefault();
      event.stopPropagation();
      setQuery("");
      setActiveIndex(0);
    }
  };

  const renderRow = (entry: HelpEntry, index: number, timeline: boolean) => {
    const Icon = ICONS[entry.id];
    const shortcut = entry.shortcutCommand
      ? shortcutLabelForCommand(keybindings, entry.shortcutCommand)
      : null;
    return (
      <QuickMenuRow
        key={entry.id}
        role="option"
        aria-selected={index === activeIndex}
        active={index === activeIndex}
        tabIndex={-1}
        onMouseMove={() => setActiveIndex(index)}
        onClick={() => go(entry)}
        hint={shortcut}
        icon={
          timeline ? (
            // The dot sits on the dashed spine drawn behind the list.
            <span className="relative z-[1] flex size-4 items-center justify-center">
              <span
                className={cn(
                  "size-1.5 rounded-full",
                  index === activeIndex ? "bg-popover-foreground" : "bg-muted-foreground/70",
                )}
              />
            </span>
          ) : Icon ? (
            <Icon aria-hidden />
          ) : (
            <span className="size-4" />
          )
        }
      >
        {entry.label}
      </QuickMenuRow>
    );
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <Tooltip>
        <TooltipTrigger
          render={
            <PopoverTrigger
              aria-label="Help"
              data-active={open || undefined}
              className="relative z-[1] mb-1.5 flex size-7 shrink-0 cursor-pointer items-center justify-center rounded-full text-[13px] font-medium text-[var(--sidebar-icon-color)] ring-1 ring-[color-mix(in_srgb,var(--sidebar-foreground)_22%,transparent)] outline-hidden transition-colors hover:bg-sidebar-row-hover hover:text-sidebar-foreground focus-visible:ring-2 focus-visible:ring-ring data-[active]:bg-sidebar-row-hover data-[active]:text-sidebar-foreground"
            />
          }
        >
          ?
        </TooltipTrigger>
        <TooltipPopup side="right">Help</TooltipPopup>
      </Tooltip>
      <PopoverPopup
        side="top"
        align="start"
        sideOffset={8}
        aria-label="Help"
        className={QUICK_MENU_POPUP_CLASS}
        viewportClassName={QUICK_MENU_VIEWPORT_CLASS}
      >
        {/* Collapsed until there is something in it: the menu looks like a
            menu, and the first keystroke turns it into a search. */}
        <div
          className={cn(
            "flex items-center gap-3 overflow-hidden px-2.5 transition-[height,opacity,margin] duration-150",
            searching ? "mb-1 h-9 border-b border-border/60 opacity-100" : "h-0 opacity-0",
          )}
        >
          <SearchIcon aria-hidden className="size-4 shrink-0 text-muted-foreground" />
          <input
            ref={inputRef}
            value={query}
            onChange={(event) => {
              setQuery(event.currentTarget.value);
              setActiveIndex(0);
            }}
            onKeyDown={onKeyDown}
            aria-label="Search help"
            role="combobox"
            aria-expanded
            aria-controls="help-menu-list"
            placeholder="Search help…"
            className="h-full min-w-0 flex-1 bg-transparent text-[13px] outline-none placeholder:text-muted-foreground"
          />
        </div>

        <div id="help-menu-list" role="listbox" aria-label="Help">
          {searching ? (
            rows.length > 0 ? (
              rows.map((entry, index) => renderRow(entry, index, false))
            ) : (
              <p className="px-2.5 py-5 text-center text-xs text-muted-foreground">
                Nothing here matches. Try the guide or search everything.
              </p>
            )
          ) : (
            <>
              {HELP_ENTRIES.map((entry, index) => renderRow(entry, index, false))}
              <p className="px-2.5 pt-2.5 pb-1 text-xs font-medium text-muted-foreground">What's new</p>
              <div className="relative">
                {/* The spine: dashed, from the first dot to the last. */}
                <span
                  aria-hidden
                  className="pointer-events-none absolute top-[18px] bottom-[18px] left-[calc(0.625rem+0.5rem-0.5px)] border-l border-dashed border-muted-foreground/40"
                />
                {WHATS_NEW.map((entry, index) => renderRow(entry, HELP_ENTRIES.length + index, true))}
              </div>
            </>
          )}
        </div>
      </PopoverPopup>
    </Popover>
  );
}
